import React, {FC, useState, useEffect} from 'react'
import {showSwal} from '../../../../ui-kit'
import {useParams, useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {CoefIoLm} from '../coef-io-lm/core/_models'
import {OecdDescription} from '../oecd-descriptions/core/_models'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {
  getCoefIoLmById,
  createCoefIoLm,
  updateCoefIoLmById,
} from '../coef-io-lm/core/_requests'
import {getAllOecdDescriptions} from '../oecd-descriptions/core/_requests'
import CoefIoLmDetailView from './components/CoefIoLmDetailView'
import OecdDropdown from './components/OecdDropdown'
import EconDropdown from '../../../../ui-kit/EconDropdown/EconDropdown'

const CoefIoLmDetailPage: FC<{subject: string}> = ({subject}) => {
  const {idCoefIoLm} = useParams<{idCoefIoLm: string}>()
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const ability = useAbility(AbilityContext)

  const isAddMode = !idCoefIoLm
  const isDetailMode = !!idCoefIoLm
  const [isEditMode, setIsEditMode] = useState(isAddMode)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [oecdList, setOecdList] = useState<OecdDescription[]>([])

  const [detail, setDetail] = useState<CoefIoLm>({
    input: null,
    output: null,
    coef: null,
    year: 2020,
    iso: 'KHM',
  })

  // Load OECD descriptions for react-select options
  useEffect(() => {
    getAllOecdDescriptions(axiosPrivate)
      .then((res) => {
        const list: OecdDescription[] = res.data.table_data || []
        setOecdList(list)
      })
      .catch((e) => {
        console.error('Failed to load OECD descriptions:', e)
      })
  }, [])

  // Load detail record
  useEffect(() => {
    const loadDetail = async () => {
      if (!isDetailMode || !idCoefIoLm) return
      setIsLoading(true)
      try {
        const res = await getCoefIoLmById(axiosPrivate, idCoefIoLm)
        const data = res.data.data
        if (data) {
          setDetail({
            input: data.input || null,
            output: data.output || null,
            coef: data.coef ?? null,
            year: data.year ?? 2020,
            iso: data.iso || 'KHM',
          })
        }
      } catch (err) {
        console.error('Failed to load Coef IO LM detail:', err)
        showSwal('Error', 'Failed to load Coef IO LM details.', 'error')
      } finally {
        setIsLoading(false)
      }
    }
    loadDetail()
  }, [idCoefIoLm])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setDetail((prev) => ({
      ...prev,
      [name]:
        name === 'iso'
          ? value.toUpperCase().slice(0, 3)
          : name === 'year'
          ? parseInt(value, 10) || 2020
          : name === 'coef'
          ? value === '' ? null : parseFloat(value)
          : value,
    }))
  }

  const handleOecdSelect =
    (field: 'input' | 'output') => (code: string | null) => {
      setDetail((prev) => ({...prev, [field]: code}))
    }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!detail.iso.trim() || !detail.year) {
      showSwal('Warning', 'ISO and Year are required.', 'warning')
      return
    }

    showSwal({
      title: `Are you sure you want to ${isAddMode ? 'create' : 'update'} this Coefficient Input Output LM record?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)
      const payload = {
        input: detail.input || null,
        output: detail.output || null,
        coef: detail.coef ?? null,
        year: detail.year,
        iso: detail.iso.trim().toUpperCase(),
      }

      if (isAddMode) {
        createCoefIoLm(axiosPrivate, payload)
          .then((res) => {
            if (res.status === 201) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/coef-io-lm')
              })
            }
          })
          .catch((err) => {
            console.error('Failed to create Coefficient Input Output LM:', err)
            showSwal('Error', 'Failed to create Coefficient Input Output LM record.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        updateCoefIoLmById(axiosPrivate, idCoefIoLm, payload)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error('Failed to update Coefficient Input Output LM:', err)
            showSwal('Error', 'Failed to update Coefficient Input Output LM record.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/coef-io-lm')
        } else {
          setIsEditMode(false)
          window.location.reload()
        }
      }
    })
  }

  const isFormValid = !!detail.iso.trim() && !!detail.year


  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
          {title: 'Coefficient Input Output LM', path: '/coef-io-lm', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {isAddMode
          ? 'Add Coefficient Input Output LM'
          : isEditMode
          ? `Edit Coefficient Input Output LM #${idCoefIoLm}`
          : `Coefficient Input Output LM Detail`}
      </PageTitle>

      <div className='CoefIoLmDetailPage'>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-table px-2'></span>
                      <h3 className='text-gray-700'>
                        {isAddMode
                          ? 'Add New Coefficient Input Output LM'
                          : isEditMode
                          ? 'Edit Coefficient Input Output LM Details'
                          : 'Coefficient Input Output LM Detail'}
                      </h3>
                    </div>
                    {ability.can('update', subject) && isDetailMode && !isEditMode && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoading ? (
                  isEditMode ? (
                    <Form onSubmit={handleSubmit} className='form-coef-io-lm'>
                      <div className='row mb-4'>
                        {/* ISO — EconDropdown */}
                        <div className='col-md-6 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            ISO <span className='text-danger'>*</span>
                          </Form.Label>
                          <EconDropdown
                            econ={detail.iso}
                            onSelect={(value) =>
                              setDetail((prev) => ({...prev, iso: value}))
                            }
                          />
                        </div>

                        {/* Year */}
                        <div className='col-md-6 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Year <span className='text-danger'>*</span>
                          </Form.Label>
                          <Form.Control
                            type='number'
                            name='year'
                            value={detail.year}
                            onChange={handleInputChange}
                            placeholder='e.g. 2020'
                            min={1900}
                            max={2100}
                            required
                          />
                        </div>

                        {/* Input — OecdDropdown with load more */}
                        <div className='col-md-6 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>Input</Form.Label>
                          <OecdDropdown
                            oecdList={oecdList}
                            value={detail.input}
                            onSelect={handleOecdSelect('input')}
                            placeholder='Search by code or description...'
                          />
                        </div>

                        {/* Output — OecdDropdown with load more */}
                        <div className='col-md-6 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>Output</Form.Label>
                          <OecdDropdown
                            oecdList={oecdList}
                            value={detail.output}
                            onSelect={handleOecdSelect('output')}
                            placeholder='Search by code or description...'
                          />
                        </div>

                        {/* Coef */}
                        <div className='col-md-6 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>Coef</Form.Label>
                          <Form.Control
                            type='number'
                            name='coef'
                            value={detail.coef ?? ''}
                            onChange={handleInputChange}
                            placeholder='Enter coefficient'
                            step='any'
                          />
                        </div>
                      </div>

                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            type='submit'
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <CoefIoLmDetailView
                      coefIoLmDetail={detail}
                      oecdList={oecdList}
                    />
                  )
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default CoefIoLmDetailPage
